import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { darken } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import clsx from "clsx";
import Popover, { PopoverProps } from "@mui/material/Popover";
import useUser from "@auth/user/useUser";
import useNavigate from "@fuse/hooks/useNavigate";
import { APP_ROUTES } from "@/configs/configRotas/app-rotas.constants";

type UserMenuProps = {
	className?: string;
	popoverProps?: Partial<PopoverProps>;
	arrowIcon?: string;
	onlyAvatar?: boolean;
};

/**
 * The user menu.
 */
function UserMenu(props: Readonly<UserMenuProps>) {
	const { className, popoverProps, arrowIcon = "lucide:chevron-up", onlyAvatar = false } = props;
	const { data: user, signOut } = useUser();
	const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);
	const displayNameRef = useRef<HTMLSpanElement | null>(null);
	const [isDisplayNameTruncated, setIsDisplayNameTruncated] = useState(false);

	//Mostra tooltip do usuario apenas se o nome dele for truncado
	useEffect(() => {
		const el = displayNameRef.current;
		if (!el) {
			setIsDisplayNameTruncated(false);
			return;
		}

		const update = () => {
			setIsDisplayNameTruncated(el.scrollWidth > el.clientWidth);
		};

		update();

		const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
		observer?.observe(el);
		window.addEventListener("resize", update);

		return () => {
			observer?.disconnect();
			window.removeEventListener("resize", update);
		};
	}, [user?.nome_exibicao]);

	const userMenuClick = (event: React.MouseEvent<HTMLElement>) => {
		setUserMenu(event.currentTarget);
	};

	const navigate = useNavigate();

	const userMenuClose = () => {
		setUserMenu(null);
	};

	if (!user) return null;

	return (
		<>
			<Button
				className={clsx(
					"user-menu flex min-w-0 shrink-0 justify-start overflow-hidden h-(--sidebar-user-height,44px) min-h-(--sidebar-user-height,44px) gap-2",
					onlyAvatar ? "min-w-0 p-0" : "",
					className,
				)}
				onClick={userMenuClick}
				color="inherit"
			>
				{user?.photoURL ? (
					<Avatar
						sx={(theme) => ({
							background: theme.vars.palette.background.default,
							color: theme.vars.palette.text.secondary,
						})}
						className={clsx(
							"avatar rounded-lg h-(--sidebar-avatar-size,28px) w-(--sidebar-avatar-size,28px) ring-1 ring-muted-foreground",
						)}
						alt="user photo"
						src={user?.photo}
						variant="rounded"
					/>
				) : (
					<Avatar
						sx={(theme) => ({
							background: (theme) => darken(theme.palette.background.default, 0.05),
							color: theme.vars.palette.text.secondary,
						})}
						className={clsx(
							"avatar h-(--sidebar-avatar-size,28px) w-(--sidebar-avatar-size,28px) text-xs ring-1 ring-muted-foreground",
						)}
					>
						{user?.nome_exibicao?.[0]}
					</Avatar>
				)}
				{!onlyAvatar && (
					<>
						<div className={clsx("flex min-w-0 flex-auto flex-col overflow-hidden gap-1")}>
							<Tooltip
								title={user?.nome_exibicao ?? ""}
								disableHoverListener={!isDisplayNameTruncated}
								disableFocusListener={!isDisplayNameTruncated}
								disableTouchListener={!isDisplayNameTruncated}
							>
								<Typography
									ref={displayNameRef}
									component="span"
									className={clsx(
										"title block truncate leading-none font-semibold tracking-tight capitalize text-(length:--sidebar-user-font-size,13px)",
									)}
								>
									{user?.nome_exibicao}
								</Typography>
							</Tooltip>
							<Typography
								className={clsx("block truncate leading-none font-medium tracking-tighter text-sm")}
								color="text.secondary"
							>
								{user?.nome_cad_grupo_usuario}
							</Typography>
						</div>
						<div className="flex shrink-0 items-center gap-2">
							<FuseSvgIcon className="arrow" size={14}>
								{arrowIcon}
							</FuseSvgIcon>
						</div>
					</>
				)}
			</Button>
			<Popover
				open={Boolean(userMenu)}
				anchorEl={userMenu}
				onClose={userMenuClose}
				anchorOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				classes={{
					paper: "min-w-32",
				}}
				{...popoverProps}
			>
				<>
					<MenuItem
						onClick={async () => {
							await navigate(APP_ROUTES.config.conta.url);
							userMenuClose();
						}}
					>
						<ListItemIcon>
							<FuseSvgIcon>lucide:settings</FuseSvgIcon>
						</ListItemIcon>
						<ListItemText primary="Configurações da conta" />
					</MenuItem>
					<MenuItem
						onClick={() => {
							signOut();
							userMenuClose();
						}}
					>
						<ListItemIcon>
							<FuseSvgIcon>lucide:square-arrow-right</FuseSvgIcon>
						</ListItemIcon>
						<ListItemText primary="Sair da conta" />
					</MenuItem>
				</>
			</Popover>
		</>
	);
}

export default UserMenu;
